import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getCompileStatusBadge } from '../../utils/compileStatus';
import {
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Card,
  CardContent,
  CardActions,
  Skeleton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  Visibility as ViewIcon,
  Settings as ConfigureIcon,
  Build as BuildIcon,
  FilterListOff as FilterListOffIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import EmptyState from '../../components/common/EmptyState';
import { motion } from 'framer-motion';
import {
  fetchStrategies,
  deleteStrategy,
  setFilters,
  clearSuccessMessage
} from './visualStrategiesSlice';
import { CardSkeleton, TableSkeleton } from '../../components/LoadingSpinner';
import { EvaluationStatusBanner } from '../evaluations';
import DWLFLoader from '../../components/DWLFLoader';
import PageHeader from '../../components/PageHeader';
import HypotheticalLegend from '../backtests/results/HypotheticalLegend';
import { getMyQuotas } from '../../api/quotas';

const StrategiesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    strategies,
    strategiesLoading,
    strategiesError,
    filters,
    nextCursor,
    hasMore,
    isDeleting,
    successMessage
  } = useSelector((state) => state.visualStrategies);

  // Local state
  const [localFilters, setLocalFilters] = useState(filters);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [strategyToDelete, setStrategyToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [pageCursors, setPageCursors] = useState([null]);
  const [currentPage, setCurrentPage] = useState(0);
  const [quotaInfo, setQuotaInfo] = useState(null);

  // Check if any filters are active (use applied filters from Redux, not local state)
  const hasActiveFilters = filters.complexity || filters.timeframe || filters.tags.length > 0;

  // Fetch strategies when filters change
  useEffect(() => {
    dispatch(fetchStrategies(filters));
  }, [dispatch, filters]);

  // Fetch quota information once on mount
  useEffect(() => {
    getMyQuotas().then(data => {
      const strategiesQuota = data.quotas?.visualStrategies || data.quotas?.strategies;
      if (strategiesQuota) {
        setQuotaInfo(strategiesQuota);
      }
    }).catch(err => {
      console.error('Failed to fetch quotas:', err);
    });
  }, [dispatch]);

  useEffect(() => {
    if (successMessage) {
      setSnackbarOpen(true);
      // Auto-hide after 4 seconds (longer for compilation messages)
      setTimeout(() => {
        dispatch(clearSuccessMessage());
        setSnackbarOpen(false);
      }, 4000);
    }
  }, [successMessage, dispatch]);

  const handleFilterChange = (field, value) => {
    setLocalFilters({ ...localFilters, [field]: value });
  };

  const handleApplyFilters = () => {
    const appliedFilters = { ...localFilters, cursor: null };
    dispatch(setFilters(appliedFilters));
    dispatch(fetchStrategies(appliedFilters));
    // Reset pagination state
    setPageCursors([null]);
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      limit: 50,
      cursor: null,
      tags: [],
      complexity: '',
      timeframe: ''
    };
    setLocalFilters(clearedFilters);
    dispatch(setFilters(clearedFilters));
    dispatch(fetchStrategies(clearedFilters));
    // Reset pagination state
    setPageCursors([null]);
    setCurrentPage(0);
  };

  const handlePageChange = (event, newPage) => {
    if (newPage > currentPage) {
      // Going forward - use nextCursor
      if (!hasMore || !nextCursor) return;
      const newFilters = { ...filters, cursor: nextCursor };
      dispatch(setFilters(newFilters));
      dispatch(fetchStrategies(newFilters));
      // Save current cursor before moving forward
      setPageCursors([...pageCursors, nextCursor]);
    } else if (newPage < currentPage) {
      // Going backward - retrieve cursor from page history
      const previousCursor = pageCursors[newPage];
      if (previousCursor === undefined) return;
      const newFilters = { ...filters, cursor: previousCursor };
      dispatch(setFilters(newFilters));
      dispatch(fetchStrategies(newFilters));
      // Truncate pageCursors to current position
      setPageCursors(pageCursors.slice(0, newPage + 1));
    }
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    const newFilters = { ...filters, limit: newLimit, cursor: null };
    dispatch(setFilters(newFilters));
    dispatch(fetchStrategies(newFilters));
    setPageCursors([null]);
    setCurrentPage(0);
  };

  const handleCreateNew = () => {
    navigate('/strategies/new');
  };

  const handleEdit = (strategyId) => {
    navigate(`/strategies/${strategyId}/edit`);
  };

  const handleView = (strategyId) => {
    navigate(`/strategies/${strategyId}/view`);
  };

  const handleDeleteClick = (strategy) => {
    setStrategyToDelete(strategy);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (strategyToDelete) {
      dispatch(deleteStrategy(strategyToDelete.strategyId));
      setDeleteDialogOpen(false);
      setStrategyToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setStrategyToDelete(null);
  };

  const handleDuplicate = (strategy) => {
    // Navigate to create new with the strategy data pre-filled
    navigate('/strategies/new', { 
      state: { 
        duplicateFrom: strategy,
        name: `${strategy.name} (Copy)`
      }
    });
  };

  const handleConfigure = (strategyId) => {
    navigate(`/strategies/${strategyId}/configure`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getComplexityColor = (complexity) => {
    switch (complexity) {
    case 'simple': return 'success';
    case 'moderate': return 'warning';
    case 'complex': return 'error';
    default: return 'default';
    }
  };

  // Use shared utility for compile status badge
  const getCompileStatus = (strategy) => {
    const label = getCompileStatusBadge(strategy);
    if (!label) return null;
    return { label, color: 'warning' };
  };

  if (strategiesLoading && !strategies.length) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="350px" height={20} />
          </Box>
          <Skeleton variant="rectangular" width="140px" height={40} />
        </Box>
        <Skeleton variant="rectangular" width="100%" height={120} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={80} sx={{ mb: 3 }} />
        <Paper sx={{ p: 3, mb: 3 }}>
          <Skeleton variant="text" width="100px" height={24} sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '200px 200px 1fr auto auto' }, gap: 2 }}>
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" width="80px" height={40} />
            <Skeleton variant="rectangular" width="80px" height={40} />
          </Box>
        </Paper>
        <TableSkeleton rows={5} columns={6} />
      </Container>
    );
  }

  if (strategiesError) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4, borderRadius: 0 }}>
          {strategiesError}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Strategies"
        subtitle={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <span>Build and manage visual trading strategies</span>
            {quotaInfo && (
              <Chip
                label={`${quotaInfo.used ?? strategies.length}/${quotaInfo.limit ?? '∞'} strategies used`}
                size="small"
                sx={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.75rem',
                  backgroundColor: (quotaInfo.used ?? strategies.length) >= (quotaInfo.limit ?? Infinity) 
                    ? 'rgba(255, 87, 34, 0.2)' 
                    : 'var(--dwlf-accent-20)',
                  color: (quotaInfo.used ?? strategies.length) >= (quotaInfo.limit ?? Infinity) 
                    ? '#ff5722' 
                    : 'var(--dwlf-accent)',
                  borderColor: (quotaInfo.used ?? strategies.length) >= (quotaInfo.limit ?? Infinity) 
                    ? '#ff5722' 
                    : 'var(--dwlf-accent)',
                  border: '1px solid',
                }}
              />
            )}
          </Box>
        }
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            disabled={quotaInfo && (quotaInfo.used ?? strategies.length) >= (quotaInfo.limit ?? Infinity)}
            sx={{ 
              fontFamily: '"Space Mono", monospace',
              fontSize: '0.75rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              borderRadius: 0,
              backgroundColor: 'var(--dwlf-accent)',
              color: 'var(--dwlf-bg)',
              px: 2.5,
              py: 1,
              '&:hover': {
                backgroundColor: 'var(--dwlf-text)',
              },
              '&:disabled': {
                backgroundColor: 'var(--dwlf-ink-12)',
                color: 'var(--dwlf-ink-30)',
              },
            }}
          >
            New Strategy
          </Button>
        }
      />

      {/* User Signals Callout */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card 
          sx={{ 
            mb: 3, 
            borderColor: 'var(--dwlf-up)',
            backgroundColor: 'var(--dwlf-up-05)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Typography 
                sx={{ 
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: 'var(--dwlf-up)',
                }}
              >
                Track Your Strategy Performance
              </Typography>
            </Box>
            <Typography 
              sx={{ 
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.8rem',
                color: 'var(--dwlf-ink-60)',
                mb: 2,
              }}
            >
              See how your configured strategies are performing with real-time signals
            </Typography>
            <Button
              size="small"
              onClick={() => navigate('/my-signals')}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                borderRadius: 0,
                border: '1px solid var(--dwlf-up)',
                color: 'var(--dwlf-up)',
                px: 2,
                py: 0.75,
                '&:hover': {
                  backgroundColor: 'var(--dwlf-up-10)',
                  borderColor: 'var(--dwlf-up)',
                },
              }}
            >
              View My Signals →
            </Button>
          </CardContent>
        </Card>
      </motion.div>

      <EvaluationStatusBanner type="strategies" />

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography 
            sx={{ 
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: 'var(--dwlf-ink-70)',
              mb: 2,
            }}
          >
            Filters
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '200px 200px 1fr auto auto' },
            gap: 2,
            alignItems: 'end',
          }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.8rem' }}>Complexity</InputLabel>
              <Select
                value={localFilters.complexity}
                label="Complexity"
                onChange={(e) => handleFilterChange('complexity', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="simple">Simple</MenuItem>
                <MenuItem value="moderate">Moderate</MenuItem>
                <MenuItem value="complex">Complex</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.8rem' }}>Timeframe</InputLabel>
              <Select
                value={localFilters.timeframe}
                label="Timeframe"
                onChange={(e) => handleFilterChange('timeframe', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1D">Daily</MenuItem>
                <MenuItem value="1W">Weekly</MenuItem>
                <MenuItem value="1M">Monthly</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Tags (comma-separated)"
              value={localFilters.tags.join(', ')}
              onChange={(e) => handleFilterChange('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
              InputLabelProps={{
                sx: { fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.8rem' }
              }}
            />
            <Button 
              variant="contained" 
              onClick={handleApplyFilters}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                borderRadius: 0,
                backgroundColor: 'var(--dwlf-accent)',
                color: 'var(--dwlf-bg)',
                px: 2,
                minWidth: 'auto',
                '&:hover': { backgroundColor: 'var(--dwlf-text)' },
              }}
            >
              Apply
            </Button>
            <Button 
              variant="outlined" 
              onClick={handleClearFilters}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                borderRadius: 0,
                borderColor: 'var(--dwlf-ink-20)',
                color: 'var(--dwlf-ink-60)',
                px: 2,
                minWidth: 'auto',
                '&:hover': {
                  borderColor: 'var(--dwlf-accent)',
                  color: 'var(--dwlf-accent)',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Clear
            </Button>
          </Box>
        </Paper>
      </motion.div>

      {/* Strategies Table - render empty state OR table based on data */}
      {strategies.length === 0 && !strategiesLoading ? (
        hasActiveFilters ? (
          // Empty state when filters are active but no results
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <EmptyState
              icon={<FilterListOffIcon sx={{ fontSize: 64 }} />}
              title="No results match your filters"
              subtitle="Try adjusting your filters or clear them to see all strategies."
              buttonLabel="Clear Filters"
              onAction={handleClearFilters}
            />
          </motion.div>
        ) : (
          // Empty state when no strategies exist at all
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <EmptyState
              icon={<BuildIcon sx={{ fontSize: 64 }} />}
              title="No strategies yet"
              subtitle="Build your first strategy using the visual builder."
              buttonLabel="Create Strategy"
              onAction={() => navigate('/strategies/new')}
              showAddIcon
            />
          </motion.div>
        )
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Last Backtest</TableCell>
                  <TableCell>Complexity</TableCell>
                  <TableCell>Last Modified</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {strategies.map((strategy) => {
                  const compileStatus = getCompileStatus(strategy);
                  return (
                  <TableRow key={strategy.strategyId} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {strategy.name}
                      </Typography>
                      {compileStatus && (
                        <Chip
                          label={compileStatus.label}
                          color={compileStatus.color}
                          size="small"
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: '20px'
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {strategy.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {strategy.lastBacktest ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {strategy.lastBacktest.winRate != null && (
                            <Chip
                              label={`WR: ${strategy.lastBacktest.winRate.toFixed(1)}%`}
                              size="small"
                              sx={{ 
                                fontSize: '0.7rem',
                                backgroundColor: strategy.lastBacktest.winRate >= 50 
                                  ? 'rgba(76, 175, 80, 0.2)' 
                                  : 'rgba(244, 67, 54, 0.2)',
                                color: strategy.lastBacktest.winRate >= 50 ? '#4caf50' : '#f44336',
                              }}
                            />
                          )}
                          {strategy.lastBacktest.profitFactor != null && (
                            <Chip
                              label={`PF: ${strategy.lastBacktest.profitFactor.toFixed(2)}`}
                              size="small"
                              sx={{ 
                                fontSize: '0.7rem',
                                backgroundColor: strategy.lastBacktest.profitFactor >= 1 
                                  ? 'rgba(76, 175, 80, 0.2)' 
                                  : 'rgba(244, 67, 54, 0.2)',
                                color: strategy.lastBacktest.profitFactor >= 1 ? '#4caf50' : '#f44336',
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {strategy.lastBacktest.trades || 0} trades
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                        Not tested yet
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={strategy.stats?.complexity || 'Unknown'}
                      color={getComplexityColor(strategy.stats?.complexity)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(strategy.updatedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Tooltip title="Quick Backtest">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/backtests/new?strategyId=${strategy.strategyId}`)}
                          sx={{ color: 'var(--dwlf-accent)' }}
                        >
                          <SpeedIcon />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => handleView(strategy.strategyId)}
                        title="View"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(strategy.strategyId)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDuplicate(strategy)}
                        title="Duplicate"
                      >
                        <DuplicateIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleConfigure(strategy.strategyId)}
                        title="Configure Symbols"
                        color="primary"
                      >
                        <ConfigureIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(strategy)}
                        disabled={isDeleting}
                        title="Delete"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
          
          {strategies.length > 0 && (
            <TablePagination
              component="div"
              count={hasMore ? -1 : 0} // -1 for unknown, 0 when no more pages
              page={currentPage}
              onPageChange={handlePageChange}
              rowsPerPage={filters.limit}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </TableContainer>
        <HypotheticalLegend sx={{ mt: 0.75 }} />
        </motion.div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Strategy</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{strategyToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="success" 
          sx={{
            backgroundColor: successMessage?.includes('compiled') ? '#10b981' : '#4caf50',
            color: 'white',
            fontWeight: 600,
            fontSize: '1rem',
            '& .MuiAlert-icon': {
              color: 'white'
            },
            '& .MuiAlert-message': {
              color: 'white'
            },
            '& .MuiAlert-action': {
              color: 'white'
            }
          }}
          onClose={() => setSnackbarOpen(false)}
        >
          <span style={{ color: 'white' }}>
            {successMessage?.includes('compiled') ? '🎉 ' : '✅ '}{successMessage}
          </span>
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StrategiesPage; 
